import Polyfill from "../Polyfill";
import {CartItemRequest, CartProcessor} from "./CartProcessor";
import {Events} from "./CartEvents";
import {QuantityControl} from "./QuantityControl";
import {CartFeedback} from "./CartFeedback";
import {countText} from "./countText";
import {TabStrip} from "../utils/TabStrip";

/**
 * Brings the format dialog for several images to life.
 *
 * Nothing is sent while the quantities are changed. "Add to cart" builds
 * one request with every image times every format with a quantity and
 * sends it in one go; afterwards the dialog shows the success message with
 * the buttons to go on shopping or to the cart.
 */
export class MultiAdd2CartDialogContent {
    static CONTAINER_SELECTOR = '.imagetypeselection-multi'
    static ADD_BUTTON_SELECTOR = '.eventgallery-add-selection'
    static CART_BUTTON_SELECTOR = '.eventgallery-opencart'
    static SELECT_STATE_SELECTOR = '.eg-multi-actions-select'
    static DONE_STATE_SELECTOR = '.eg-multi-actions-done'
    static SUCCESS_SELECTOR = '.eventgallery-multi-success'
    static PARTIAL_SELECTOR = '.eventgallery-multi-partial-error'
    static ERROR_SELECTOR = '.eventgallery-cart-error'
    static FORMATS_SELECTOR = '.imagetypeselection'

    cartProcessor: CartProcessor;
    container: HTMLElement;
    items: {folder: string, file: string}[] = [];
    quantityControl: QuantityControl;

    addButton: HTMLButtonElement;
    cartButton: HTMLButtonElement;

    private isSending: boolean = false;
    private failed: boolean = false;
    private pendingHref: string | null = null;

    /**
     * @param root the dialog with the content
     * @param signal the signal of its Overlay: the listeners on the document go when the dialog
     *               closes (until 7.0.0 every opening left three of them behind)
     */
    constructor(cartProcessor: CartProcessor, root: ParentNode, signal?: AbortSignal) {
        this.cartProcessor = cartProcessor;
        this.container = root.querySelector(MultiAdd2CartDialogContent.CONTAINER_SELECTOR);
        if (!this.container) {
            return;
        }

        try {
            this.items = JSON.parse(this.container.getAttribute('data-items') || '[]');
        } catch (e) {
            this.items = [];
        }

        this.addButton = this.container.querySelector(MultiAdd2CartDialogContent.ADD_BUTTON_SELECTOR);
        this.cartButton = this.container.querySelector(MultiAdd2CartDialogContent.CART_BUTTON_SELECTOR);

        TabStrip.bindAll(this.container);

        this.quantityControl = new QuantityControl(this.container, {
            debounceMs: 0,
            onChange: () => this.refresh(),
            onCommit: () => this.refresh()
        });

        if (this.addButton) {
            this.addButton.addEventListener('click', (e) => this.addSelection(e));
        }
        if (this.cartButton) {
            this.cartButton.addEventListener('click', (e) => this.openCart(e));
        }

        const listening = {signal: signal};
        document.addEventListener(Events.error, () => { this.failed = true; }, listening);
        document.addEventListener(Events.reload, (e: CustomEvent) => CartFeedback.update(this.container, e.detail), listening);
        document.addEventListener(Events.busy, (e: CustomEvent) => {
            if (e.detail?.busy === false && this.pendingHref) {
                const href = this.pendingHref;
                this.pendingHref = null;
                document.location.href = href;
            }
        }, listening);

        CartFeedback.update(this.container, this.cartProcessor.getLastCartData());
        this.refresh();
    }

    /**
     * Number of items the request would add: every quantity applies to
     * every image.
     */
    totalCount(): number {
        let perImage = 0;
        this.quantityControl.getInputs().forEach((input) => {
            perImage += this.quantityControl.getValue(input);
        });
        return perImage * this.items.length;
    }

    refresh() {
        if (!this.addButton) {
            return;
        }
        const total = this.totalCount();
        this.addButton.disabled = total === 0 || this.isSending;

        const label = countText(this.addButton, 'data-template', total);
        if (total > 0 && label) {
            MultiAdd2CartDialogContent.textTarget(this.addButton, '.eg-btn__label').textContent = label;
        }
    }

    /**
     * Where a text goes: the part of the element meant for it when there is
     * one (the icon beside it stays), otherwise the element itself, as an
     * override of the snippet from an earlier version prints it.
     */
    static textTarget(element: HTMLElement, selector: string): HTMLElement {
        return (element.querySelector(selector) as HTMLElement) || element;
    }

    buildRequests(): CartItemRequest[] {
        const requests: CartItemRequest[] = [];
        this.quantityControl.getInputs().forEach((input) => {
            const quantity = this.quantityControl.getValue(input);
            const imagetypeid = parseInt(input.getAttribute('data-imagetypeid') || '0');
            if (input.disabled || quantity <= 0 || !imagetypeid) {
                return;
            }
            this.items.forEach((item) => {
                requests.push({folder: item.folder, file: item.file, imagetypeid: imagetypeid, quantity: quantity});
            });
        });
        return requests;
    }

    addSelection(e: Event) {
        e.preventDefault();
        const requests = this.buildRequests();
        if (requests.length === 0 || this.isSending) {
            return;
        }

        this.isSending = true;
        this.failed = false;
        this.hide(MultiAdd2CartDialogContent.ERROR_SELECTOR);
        this.refresh();

        this.cartProcessor.addItems(requests).then(() => {
            this.isSending = false;
            if (this.failed) {
                this.show(MultiAdd2CartDialogContent.ERROR_SELECTOR);
                this.refresh();
                return;
            }
            this.showSuccess();
        });
    }

    /**
     * The server may have turned some images down, e.g. one which was
     * unpublished after the page was loaded. The message counts what went
     * in; a second line names how many did not.
     */
    showSuccess() {
        const bulk = this.cartProcessor.getLastCartData()?.bulk;
        const failed = bulk ? Math.min(bulk.failed, this.items.length) : 0;
        const added = this.items.length - failed;

        const success: HTMLElement = this.container.querySelector(MultiAdd2CartDialogContent.SUCCESS_SELECTOR);
        if (success && added > 0) {
            MultiAdd2CartDialogContent.textTarget(success, '.eg-notice__body').textContent = countText(success, 'data-template', added);
            success.hidden = false;
        }

        const partial: HTMLElement = this.container.querySelector(MultiAdd2CartDialogContent.PARTIAL_SELECTOR);
        if (partial && failed > 0) {
            MultiAdd2CartDialogContent.textTarget(partial, '.eg-notice__body').textContent = countText(partial, 'data-template', failed);
            partial.hidden = false;
        }

        this.hide(MultiAdd2CartDialogContent.SELECT_STATE_SELECTOR);
        this.show(MultiAdd2CartDialogContent.DONE_STATE_SELECTOR);
        // "Add to cart", which had the focus, is hidden now: the focus goes to "View cart"
        this.cartButton?.focus();

        // The quantities are in the cart now and cannot be changed here any
        // more, so the list of formats only takes up room. What remains is
        // the strip of thumbnails, the message, the cart line and the buttons.
        this.hide(MultiAdd2CartDialogContent.FORMATS_SELECTOR);

        document.dispatchEvent(Polyfill.createNewEvent(Events.selectionAdded, {count: added}));
    }

    /**
     * "Buy now": goes to the cart once no request is on its way any more.
     */
    openCart(e: Event) {
        e.preventDefault();
        const href = this.cartButton.getAttribute('data-href');
        if (!href) {
            return;
        }
        if (this.cartProcessor.isBusy()) {
            this.pendingHref = href;
            return;
        }
        document.location.href = href;
    }

    private show(selector: string) {
        const element: HTMLElement = this.container.querySelector(selector);
        if (element) element.hidden = false;
    }

    private hide(selector: string) {
        const element: HTMLElement = this.container.querySelector(selector);
        if (element) element.hidden = true;
    }
}
